import { useEffect, useState, useRef, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Save, Plus, FileText, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const TOOLS = [
  { cmd: "bold", icon: Bold, testid: "doc-editor-bold-btn" },
  { cmd: "italic", icon: Italic, testid: "doc-editor-italic-btn" },
  { cmd: "underline", icon: Underline, testid: "doc-editor-underline-btn" },
  { cmd: "insertUnorderedList", icon: List, testid: "doc-editor-ul-btn" },
  { cmd: "insertOrderedList", icon: ListOrdered, testid: "doc-editor-ol-btn" },
  { cmd: "formatBlock", arg: "H1", icon: Heading1, testid: "doc-editor-h1-btn" },
  { cmd: "formatBlock", arg: "H2", icon: Heading2, testid: "doc-editor-h2-btn" },
];

export default function PolicyEditor({ isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [title, setTitle] = useState("");
  const editorRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/policies");
      setDocs(res.data);
      if (res.data.length && !active) selectDoc(res.data[0]);
    } catch (err) { toast.error(apiErrorMessage(err)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectDoc = (d) => {
    setActive(d);
    setTitle(d.title);
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = d.content || ""; }, 0);
  };

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const newDoc = async () => {
    try {
      const res = await api.post("/policies", { title: "Untitled Document", content: "" });
      setDocs((d) => [res.data, ...d]);
      selectDoc(res.data);
      toast.success("Document created");
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const save = async () => {
    if (!active) return;
    try {
      const content = editorRef.current?.innerHTML || "";
      const res = await api.put(`/policies/${active.id}`, { title: title || "Untitled Document", content });
      setDocs((d) => d.map((x) => (x.id === res.data.id ? res.data : x)));
      setActive(res.data);
      toast.success("Saved");
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/policies/${id}`);
      const rest = docs.filter((d) => d.id !== id);
      setDocs(rest);
      if (active?.id === id) { setActive(null); setTitle(""); if (editorRef.current) editorRef.current.innerHTML = ""; if (rest[0]) selectDoc(rest[0]); }
      toast.success("Deleted");
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Policy & Notes</h1>
          <p className="text-sm text-slate-500 mt-1">Department SOPs and notes — edited like a Word document.</p>
        </div>
        {isAdmin && <Button onClick={newDoc} data-testid="new-doc-button" className="gap-2 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />New Document</Button>}
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <div className="space-y-1.5">
          {docs.length === 0 && <p className="text-sm text-slate-400 px-2">No documents yet.</p>}
          {docs.map((d) => (
            <button key={d.id} onClick={() => selectDoc(d)} data-testid={`doc-list-item-${d.id}`}
              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-2 transition-colors ${active?.id === d.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate flex-1">{d.title}</span>
            </button>
          ))}
        </div>

        {active ? (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-1.5 p-3 border-b border-slate-200 bg-slate-50">
              {isAdmin && TOOLS.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.testid} data-testid={t.testid} onMouseDown={(e) => { e.preventDefault(); exec(t.cmd, t.arg); }}
                    className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-200 transition-colors">
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
              <div className="flex-1" />
              {isAdmin && (
                <>
                  <Button size="sm" onClick={save} data-testid="doc-editor-save-btn" className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Save className="h-4 w-4" />Save</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(active.id)} data-testid="doc-editor-delete-btn" className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                </>
              )}
            </div>
            <div className="p-6 sm:p-10 bg-slate-100/50">
              <div className="max-w-3xl mx-auto bg-white border border-slate-200 shadow-md rounded-lg p-8 sm:p-12 min-h-[560px]">
                {isAdmin ? (
                  <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input"
                    className="w-full text-2xl font-bold text-slate-900 outline-none mb-4 font-display" placeholder="Document title" />
                ) : (
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">{active.title}</h2>
                )}
                <div
                  ref={editorRef}
                  contentEditable={isAdmin}
                  suppressContentEditableWarning
                  data-testid="doc-editor-content"
                  data-placeholder="Start writing your policy…"
                  className="doc-editor text-slate-800 leading-relaxed min-h-[400px] text-[15px]"
                />
                <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">
                  Last updated by {active.updated_by} · {new Date(active.updated_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white/50 py-24 flex flex-col items-center justify-center text-slate-400">
            <FileText className="h-10 w-10 mb-3" strokeWidth={1.5} />
            <p className="text-sm font-medium">{isAdmin ? "Create a document to get started." : "No policy documents available yet."}</p>
          </div>
        )}
      </div>
    </div>
  );
}
